import React from 'react';

import { Icon } from 'ui';
import Loader from 'ui/loader/Loader';
import { useId } from 'ui/utils';

export type InputType = 'text' | 'email' | 'number' | 'password' | 'tel' | 'url' | 'datetime-local' | 'date' | 'search';

type Props = {
  id?: string;
  name?: string;
  autoFocus?: boolean;
  autoComplete?: string | 'on' | 'off';
  type?: InputType;
  textAlign?: 'left' | 'right';
  required?: boolean;
  value?: string | Date | number;
  icon?: React.ComponentProps<typeof Icon>['name'];
  attached?: 'left' | 'both' | 'right';
  // iconPosition?: 'left' | 'right'; // TODO
  size?: 'basic' | 'large';
  placeholder?: string;
  error?: string;
  loading?: boolean;
  readOnly?: boolean;
  maxLength?: number;
  max?: number | string;
  min?: number | string;
  roundedFull?: boolean;
  classNames?: (classes: string[]) => string[];
  onChange?: (newValue: string) => void;
  onClear?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onKeyPress?: React.KeyboardEventHandler<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
};

function Input({
  id,
  name,
  autoFocus,
  autoComplete = 'on',
  icon,
  type = 'text',
  textAlign = 'left',
  required = false,
  attached,
  size = 'basic',
  value = '',
  placeholder,
  error,
  loading,
  readOnly,
  classNames = (classes) => classes,
  onChange,
  onClear,
  onFocus,
  onBlur,
  maxLength,
  min,
  max,
  onKeyPress,
  onKeyDown,
  roundedFull,
}: Props) {
  const internalId = useId();

  if (!name) {
    name = `input-${internalId}`;
  }

  if (!id) {
    id = `${name}__id`;
  }

  const helpTextId = `${id}__description`;

  const inputClasses = ['text-gray-900', 'shadow-sm', 'block', 'w-full', 'placeholder-gray-300'];

  if (size === 'basic') {
    inputClasses.push('sm:text-sm');
  } else {
    inputClasses.push('text-basic px-4 py-3');
  }

  if (error) {
    inputClasses.push(
      'border-red-300',
      'text-red-900',
      'placeholder-red-300',
      'focus:outline-none',
      'focus:ring-red-500',
      'focus:border-red-500',
    );
  } else {
    inputClasses.push('focus:ring-indigo-500', 'focus:border-indigo-500', 'border-gray-300');
  }

  // if (button) {
  //   inputClasses.push('rounded-l-md');
  // } else {
  //   inputClasses.push('rounded-md');
  // }

  switch (attached) {
    case 'left':
      if (roundedFull) {
        inputClasses.push('rounded-r-full border-l-0');
      } else inputClasses.push('rounded-r-md border-l-0');
      break;
    case 'right':
      if (roundedFull) {
        inputClasses.push('rounded-l-full border-r-0');
      } else inputClasses.push('rounded-l-md border-r-0');
      break;
    case 'both':
      inputClasses.push('rounded-none border-l-0 border-r-0');
      break;
    default:
      if (roundedFull) {
        inputClasses.push('rounded-full');
      } else inputClasses.push('rounded-md');
      break;
  }

  if (textAlign === 'right') {
    inputClasses.push('text-right');
  }

  const iconClasses = [];
  if (icon) {
    if (size === 'basic') {
      inputClasses.push('pl-10');
      iconClasses.push('h-5 w-5 text-gray-400');
    } else {
      inputClasses.push('pl-12');
      iconClasses.push('h-7 w-7 text-gray-400');
    }
  }

  if (onClear) {
    inputClasses.push('pr-10');
  }

  // types fetched from: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input
  const inputTypes = [
    'text',
    'password',
    'text',
    'button',
    'email',
    'color',
    'date',
    'file',
    'hidden',
    'radio',
    'range',
    'tel',
    'time',
    'url',
    'checkbox',
    'datetime-local',
    'image',
    'month',
    'number',
    'reset',
    'search',
    'submit',
    'week',
  ];

  // we want the error exclamation icon to be on the other side of text
  const errorClasses = ['pointer-events-none', 'absolute', 'inset-y-0', 'flex', 'items-center', 'pr-3'];

  if (textAlign === 'left') {
    errorClasses.push('right-0');
  } else {
    errorClasses.push('left-2');
  }

  return (
    <div className="relative flex flex-grow items-stretch focus-within:z-10">
      {icon && (
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <Icon name={icon} className={iconClasses.join(' ')} />
        </div>
      )}
      <input
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        type={inputTypes.includes(type) ? type : 'text'}
        name={name}
        id={id}
        maxLength={maxLength}
        min={min}
        max={max}
        readOnly={readOnly}
        className={classNames(inputClasses.flatMap((cls) => cls.split(' '))).join(' ')}
        placeholder={placeholder}
        value={value}
        required={required}
        onChange={(e) => onChange && onChange(e.target.value)}
        aria-describedby={helpTextId}
        onFocus={onFocus}
        onBlur={onBlur}
        step="any"
        onKeyPress={onKeyPress}
        onKeyDown={onKeyDown}
      />
      {onClear && (
        <div className="absolute inset-y-0 right-0 flex items-center pr-3" onClick={onClear} role="button" tabIndex={0}>
          <Icon name="x" solid className="h-5 w-5 text-gray-400" />
        </div>
      )}
      {error && (
        <div className={errorClasses.join(' ')}>
          <svg
            className="h-5 w-5 text-red-500"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      )}
      {loading && (
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
          <Loader size="tiny" />
        </div>
      )}
    </div>
  );
}

export default Input;
